import type { AxiosInstance, AxiosResponse } from "axios";
import type {
  CarrierAccessApiLeg,
  CarrierAccessDetailResponse,
  CarrierAccessEnvelope,
  CarrierAccessPerformance,
} from "./carrierAccess.types";

/**
 * Carrier Access — API requests (chopper-backend CTX-650). Functions take the
 * AxiosInstance returned by `useAxiosAuth0()`, whose base URL already ends in
 * `/v1`.
 */

/**
 * GET /kontroledge/carrier-access/:id?tenantId=
 *
 * One access with its derived legs, both endpoints resolved and the term
 * that answers today.
 */
export function fetchCarrierAccessDetail(
  api: AxiosInstance,
  tenantId: number,
  id: number,
  signal?: AbortSignal
): Promise<AxiosResponse<CarrierAccessDetailResponse>> {
  return api.get<CarrierAccessDetailResponse>(
    `/kontroledge/carrier-access/${id}?tenantId=${tenantId}`,
    { signal }
  );
}

/**
 * PATCH /kontroledge/carrier-access/:id?tenantId=
 *
 * Rename. Same contract as the other services' update, minus the tag:
 * Carrier Access is tagged through TaggedResources (CTX-654), so the body
 * carries `name` only — anything else is refused with `Invalid fields`.
 */
export function updateCarrierAccessName(
  api: AxiosInstance,
  tenantId: number,
  id: number,
  name: string
): Promise<AxiosResponse<CarrierAccessEnvelope<{ id: number; name: string }>>> {
  return api.patch(`/kontroledge/carrier-access/${id}?tenantId=${tenantId}`, {
    name,
  });
}

/**
 * GET /kontroledge/carrier-access/:id/performance?tenantId=&legKind=&legId=
 *
 * Bandwidth of one carrier's VLAN, for its chart in the detail. The access
 * as a whole is the parent Port's chart. Same preset query mode as the
 * Extended PoP link charts.
 */
export function fetchCarrierAccessPerformance(
  api: AxiosInstance,
  tenantId: number,
  id: number,
  leg: Pick<CarrierAccessApiLeg, "kind" | "id">,
  interval: string,
  timeRange: string
): Promise<AxiosResponse<CarrierAccessEnvelope<CarrierAccessPerformance>>> {
  const params = new URLSearchParams({
    tenantId: String(tenantId),
    legKind: leg.kind,
    legId: String(leg.id),
    interval,
    timeRange,
  });
  return api.get(
    `/kontroledge/carrier-access/${id}/performance?${params.toString()}`
  );
}

/* ── API → UI mappers (single import path: `./requests/request`) ── */
export { mapCarrierAccessApiItemToCarrierAccessDetail } from "./mappers";

/* ── Re-export API types ── */
export type {
  CarrierAccessApiDetail,
  CarrierAccessDetailResponse,
} from "./carrierAccess.types";
